import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Copy, ShieldCheck, EyeOff, ChevronDown, ChevronUp, RefreshCw } from "lucide-react";
import api, { apiError } from "../lib/api";
import { Money, Sensitive } from "../context/PrivacyContext";
import { fmtDate, fmtEUR } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import MoneyMovementFields from "../components/MoneyMovementFields";
import ProviderSelect from "../components/ProviderSelect";
import MemberSelect, { MemberBadge } from "../components/MemberSelect";
import MovementTrace from "../components/MovementTrace";

const num = (v) => parseFloat(String(v).replace(",", ".")) || 0;
const optNum = (v) => (v === "" || v === null ? null : num(v));
const CATS = ["Hogar", "Vida", "Salud", "Coche", "Decesos", "Responsabilidad Civil", "Otros"];
const FREQS = [["anual", "Anual"], ["semestral", "Semestral"], ["trimestral", "Trimestral"], ["mensual", "Mensual"]];
const STATUSES = [["activo", "Activo"], ["pausado", "Pausado"], ["finalizado", "Finalizado"]];
const MONTHS = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
const FREQ_COUNT = { anual: 1, semestral: 2, trimestral: 4, mensual: 12 };

const EMPTY = {
  name: "", company: "", provider_id: null, member_id: null, category: "Hogar", annual_premium: "", payment_frequency: "anual",
  renewal_date: "", auto_renewal: true, coverages: "", receipts: [], capital_asegurado: "", capital_contenido: "",
  capital_continente: "", capital_fallecimiento: "", indemnizacion: "", beneficiarios: "",
  sensitive: false, status: "activo", account_id: null, payment_method_id: null, notes: "",
};

const fromItem = (s) => ({
  ...EMPTY,
  ...Object.fromEntries(Object.entries(s).filter(([k]) => k in EMPTY).map(([k, v]) => {
    if (k === "receipts") return [k, Array.isArray(v) ? v : []];
    return [k, v ?? (typeof EMPTY[k] === "boolean" ? EMPTY[k] : (k === "account_id" || k === "payment_method_id") ? null : "")];
  })),
});

function StatusBadge({ status }) {
  if (!status || status === "activo") return null;
  const cls = status === "pausado" ? "bg-amber-100 text-amber-700" : "bg-secondary text-muted-foreground";
  return <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${cls}`}>{status}</span>;
}

export default function Seguros() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [editDoc, setEditDoc] = useState(null);
  const [capitals, setCapitals] = useState(false);
  const [accounts, setAccounts] = useState([]);

  const load = () => Promise.all([
    api.get("/insurances"),
    api.get("/providers"),
  ]).then(([ins, provs]) => {
    const byId = Object.fromEntries((provs.data || []).map((p) => [p.id, p.name]));
    setItems((ins.data || []).map((s) => ({ ...s, provider_name: s.provider_id ? byId[s.provider_id] : null })));
  });
  useEffect(() => {
    load();
    api.get("/assets").then((r) => setAccounts(r.data));
  }, []);

  const submit = async () => {
    try {
      const cleanReceipts = (form.receipts || [])
        .map((r) => ({ month: parseInt(r.month) || 1, day: parseInt(r.day) || 1, amount: num(r.amount), label: r.label || "" }))
        .filter((r) => r.amount > 0);
      const useReceipts = form.payment_frequency !== "anual" && cleanReceipts.length > 0;
      const annualFromReceipts = cleanReceipts.reduce((s, r) => s + r.amount, 0);
      const payload = {
        ...form,
        annual_premium: useReceipts ? annualFromReceipts : num(form.annual_premium),
        receipts: useReceipts ? cleanReceipts : [],
        capital_asegurado: optNum(form.capital_asegurado),
        capital_contenido: optNum(form.capital_contenido),
        capital_continente: optNum(form.capital_continente),
        capital_fallecimiento: optNum(form.capital_fallecimiento),
        indemnizacion: optNum(form.indemnizacion),
        renewal_date: form.renewal_date || null,
      };
      if (editId) await api.put(`/insurances/${editId}`, payload);
      else await api.post("/insurances", payload);
      toast.success(editId ? "Seguro actualizado" : "Seguro creado");
      setOpen(false); setForm(EMPTY); setEditId(null); setCapitals(false);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (id) => { await api.delete(`/insurances/${id}`); load(); };

  const duplicateItem = (s) => {
    setForm({ ...fromItem(s), name: `${s.name} (copia)` });
    setEditId(null);
    setCapitals(true);
    setOpen(true);
    toast.info("Datos copiados. Ajusta lo que necesites y guarda");
  };

  const startEdit = (s) => {
    setEditDoc(s);
    setForm(fromItem(s));
    setEditId(s.id);
    setCapitals(true);
    setOpen(true);
  };

  const activos = (items || []).filter((s) => (s.status || "activo") === "activo");
  const totalAnual = activos.reduce((s, i) => s + (i.annual_premium || 0), 0);

  const SensOrMoney = ({ s, value, testId }) =>
    s.sensitive ? <Sensitive value={value} testId={testId} className="font-semibold tabular" /> : <Money value={value} testId={testId} className="font-semibold tabular" />;

  return (
    <div className="space-y-6" data-testid="seguros-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tighter">Seguros</h1>
          <p className="text-sm text-muted-foreground mt-1">Pólizas, coberturas y vencimientos. Sus primas alimentan tus previsiones.</p>
        </div>
        <Button data-testid="add-insurance-button" onClick={() => { setForm(EMPTY); setEditId(null); setCapitals(false); setOpen(true); }}>
          <Plus size={16} className="mr-1" /> Añadir seguro
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="border border-border rounded-lg p-5">
          <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-bold">Prima anual (activos)</div>
          <Money value={totalAnual} testId="total-primas" className="block mt-1 text-2xl sm:text-3xl font-semibold tabular" />
        </div>
        <div className="border border-border rounded-lg p-5">
          <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-bold">Equivalente mensual</div>
          <Money value={totalAnual / 12} className="block mt-1 text-2xl sm:text-3xl font-semibold tabular" />
        </div>
      </div>

      {items === null ? (
        <div className="text-sm text-muted-foreground">Cargando…</div>
      ) : items.length === 0 ? (
        <p className="text-sm text-muted-foreground border border-border rounded-lg p-8 text-center">Sin seguros registrados.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((s) => (
            <div key={s.id} className={`border border-border rounded-lg p-5 space-y-3 hover:border-foreground/30 transition-colors ${(s.status || "activo") !== "activo" ? "opacity-60" : ""}`} data-testid={`insurance-card-${s.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-heading font-bold flex items-center gap-1.5 flex-wrap">
                    <ShieldCheck size={15} /> {s.name}
                    <MemberBadge memberId={s.member_id} />
                    {s.sensitive && <EyeOff size={12} className="text-muted-foreground" title="Datos sensibles" />}
                    <StatusBadge status={s.status} />
                  </div>
                  <div className="text-xs text-muted-foreground flex items-center gap-1">
                    {s.provider_name || s.company || "—"} · {s.category}
                    {s.auto_renewal !== false && <span className="inline-flex items-center gap-0.5" title="Renovación automática"><RefreshCw size={10} /> auto</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button data-testid={`duplicate-insurance-${s.id}`} onClick={() => duplicateItem(s)} title="Duplicar" className="text-muted-foreground hover:text-foreground"><Copy size={15} /></button>
                  <button data-testid={`edit-insurance-${s.id}`} onClick={() => startEdit(s)} className="text-muted-foreground hover:text-foreground"><Pencil size={15} /></button>
                  <button data-testid={`delete-insurance-${s.id}`} onClick={() => remove(s.id)} className="text-muted-foreground hover:text-red-600"><Trash2 size={15} /></button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                <div><div className="text-xs text-muted-foreground">Prima anual</div><Money value={s.annual_premium} className="font-semibold tabular" /></div>
                <div><div className="text-xs text-muted-foreground">Forma de pago</div><span className="font-semibold capitalize">{s.payment_frequency}</span></div>
                <div><div className="text-xs text-muted-foreground">Próximo vencimiento</div><span className="font-semibold">{fmtDate(s.renewal_date)}</span></div>
                {s.account_id && <div><div className="text-xs text-muted-foreground">Cuenta de cargo</div><span className="font-semibold">{accounts.find((a) => a.id === s.account_id)?.name || "—"}</span></div>}
                {s.capital_asegurado != null && <div><div className="text-xs text-muted-foreground">Capital asegurado</div><SensOrMoney s={s} value={s.capital_asegurado} testId={`cap-asegurado-${s.id}`} /></div>}
                {s.capital_contenido != null && <div><div className="text-xs text-muted-foreground">Capital contenido</div><Money value={s.capital_contenido} className="font-semibold tabular" /></div>}
                {s.capital_continente != null && <div><div className="text-xs text-muted-foreground">Capital continente</div><Money value={s.capital_continente} className="font-semibold tabular" /></div>}
                {s.capital_fallecimiento != null && <div><div className="text-xs text-muted-foreground">Capital fallecimiento</div><SensOrMoney s={s} value={s.capital_fallecimiento} testId={`cap-fallecimiento-${s.id}`} /></div>}
                {s.indemnizacion != null && <div><div className="text-xs text-muted-foreground">Indemnización</div><SensOrMoney s={s} value={s.indemnizacion} testId={`indemnizacion-${s.id}`} /></div>}
                {s.beneficiarios && (
                  <div className="col-span-2">
                    <div className="text-xs text-muted-foreground">Beneficiarios</div>
                    {s.sensitive ? <Sensitive value={s.beneficiarios} money={false} testId={`beneficiarios-${s.id}`} className="font-semibold text-sm" /> : <span className="font-semibold">{s.beneficiarios}</span>}
                  </div>
                )}
              </div>
              {s.coverages && <div className="text-xs text-muted-foreground border-t border-border pt-2"><span className="font-semibold text-foreground">Coberturas:</span> {s.coverages}</div>}
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{editId ? "Editar seguro" : "Nuevo seguro"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input data-testid="insurance-name-input" placeholder="Nombre (ej. Seguro Hogar)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <div>
              <label className="text-xs text-muted-foreground">Pertenece a</label>
              <MemberSelect value={form.member_id} onChange={(id) => setForm({ ...form, member_id: id })} preselectCurrent={!editId} testId="insurance-member-select" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Aseguradora / Compañía</label>
              <ProviderSelect
                testId="insurance-provider-select"
                value={form.provider_id}
                onChange={(id, obj) => setForm({ ...form, provider_id: id, company: obj?.name || form.company })}
                legacyName={!form.provider_id ? form.company : ""}
                placeholder="Selecciona una aseguradora del catálogo…"
                group="Aseguradoras"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="insurance-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
              <Input data-testid="insurance-premium-input" placeholder="Prima anual (€)" inputMode="decimal" value={form.annual_premium} onChange={(e) => setForm({ ...form, annual_premium: e.target.value })} disabled={form.payment_frequency !== "anual" && (form.receipts || []).length > 0} />
              <Select value={form.payment_frequency} onValueChange={(v) => {
                const next = { ...form, payment_frequency: v };
                if (v !== "anual" && (form.receipts || []).length === 0) {
                  const count = FREQ_COUNT[v] || 1;
                  const perAmount = num(form.annual_premium) ? +(num(form.annual_premium) / count).toFixed(2) : "";
                  const stepMonths = 12 / count;
                  const base = form.renewal_date ? new Date(form.renewal_date) : new Date();
                  const startMonth = base.getMonth() + 1;
                  const day = base.getDate() || 1;
                  next.receipts = Array.from({ length: count }, (_, k) => ({
                    month: ((startMonth - 1 + Math.round(k * stepMonths)) % 12) + 1,
                    day,
                    amount: perAmount,
                    label: "",
                  }));
                }
                if (v === "anual") next.receipts = [];
                setForm(next);
              }}>
                <SelectTrigger data-testid="insurance-frequency-select"><SelectValue /></SelectTrigger>
                <SelectContent>{FREQS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground">Vencimiento / renovación</label>
                <Input data-testid="insurance-renewal-input" type="date" value={form.renewal_date} onChange={(e) => setForm({ ...form, renewal_date: e.target.value })} />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Estado</label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="insurance-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center justify-between border border-border rounded-lg px-3 py-2.5">
              <span className="text-sm">Renovación automática</span>
              <Switch data-testid="insurance-autorenewal-switch" checked={form.auto_renewal} onCheckedChange={(v) => setForm({ ...form, auto_renewal: v })} />
            </div>
            {form.payment_frequency !== "anual" && (
              <div className="border border-border rounded-lg p-3 space-y-2" data-testid="insurance-receipts-editor">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-semibold">Recibos programados</div>
                    <div className="text-xs text-muted-foreground">Define mes, día e importe de cada recibo. Se repetirán cada año.</div>
                  </div>
                  <Button type="button" variant="outline" size="sm" data-testid="insurance-add-receipt-button"
                    onClick={() => setForm({ ...form, receipts: [...(form.receipts || []), { month: 1, day: 1, amount: "", label: "" }] })}>
                    <Plus size={14} className="mr-1" /> Añadir
                  </Button>
                </div>
                {(form.receipts || []).length === 0 ? (
                  <div className="text-xs text-muted-foreground italic">Sin recibos definidos. Pulsa &quot;Añadir&quot; para programarlos.</div>
                ) : (
                  <div className="space-y-2">
                    {(form.receipts || []).map((r, idx) => (
                      <div key={idx} className="grid grid-cols-[1fr_60px_1fr_28px] gap-2 items-center" data-testid={`insurance-receipt-row-${idx}`}>
                        <Select value={String(r.month || 1)} onValueChange={(v) => {
                          const copy = [...form.receipts]; copy[idx] = { ...copy[idx], month: parseInt(v) }; setForm({ ...form, receipts: copy });
                        }}>
                          <SelectTrigger data-testid={`insurance-receipt-month-${idx}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{MONTHS.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
                        </Select>
                        <Input inputMode="numeric" placeholder="Día" value={r.day || ""} data-testid={`insurance-receipt-day-${idx}`}
                          onChange={(e) => { const copy = [...form.receipts]; copy[idx] = { ...copy[idx], day: e.target.value }; setForm({ ...form, receipts: copy }); }} />
                        <Input inputMode="decimal" placeholder="Importe (€)" value={r.amount} data-testid={`insurance-receipt-amount-${idx}`}
                          onChange={(e) => { const copy = [...form.receipts]; copy[idx] = { ...copy[idx], amount: e.target.value }; setForm({ ...form, receipts: copy }); }} />
                        <button type="button" data-testid={`insurance-receipt-remove-${idx}`} className="text-muted-foreground hover:text-red-600"
                          onClick={() => { const copy = form.receipts.filter((_, i) => i !== idx); setForm({ ...form, receipts: copy }); }}>
                          <Trash2 size={15} />
                        </button>
                      </div>
                    ))}
                    <div className="text-xs text-muted-foreground pt-1 border-t border-border">
                      Suma anual de los recibos: <span className="font-semibold text-foreground tabular">{fmtEUR((form.receipts || []).reduce((s, r) => s + num(r.amount), 0))}</span>
                    </div>
                  </div>
                )}
              </div>
            )}
            <MoneyMovementFields direction="out" values={form} onChange={(patch) => setForm({ ...form, ...patch })} testPrefix="insurance" />
            <Input data-testid="insurance-coverages-input" placeholder="Coberturas (ej. Incendio, Robo, RC)" value={form.coverages} onChange={(e) => setForm({ ...form, coverages: e.target.value })} />

            <button data-testid="insurance-capitals-toggle" onClick={() => setCapitals(!capitals)} className="flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-foreground">
              {capitals ? <ChevronUp size={13} /> : <ChevronDown size={13} />} Capitales, beneficiarios e indemnizaciones
            </button>
            {capitals && (
              <div className="space-y-3 border-t border-border pt-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-muted-foreground">Capital asegurado (€)</label>
                    <Input data-testid="insurance-asegurado-input" inputMode="decimal" value={form.capital_asegurado} onChange={(e) => setForm({ ...form, capital_asegurado: e.target.value })} />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Capital fallecimiento (€)</label>
                    <Input data-testid="insurance-fallecimiento-input" inputMode="decimal" value={form.capital_fallecimiento} onChange={(e) => setForm({ ...form, capital_fallecimiento: e.target.value })} />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Cap. contenido (€)</label>
                    <Input data-testid="insurance-contenido-input" inputMode="decimal" value={form.capital_contenido} onChange={(e) => setForm({ ...form, capital_contenido: e.target.value })} />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Cap. continente (€)</label>
                    <Input data-testid="insurance-continente-input" inputMode="decimal" value={form.capital_continente} onChange={(e) => setForm({ ...form, capital_continente: e.target.value })} />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Indemnización (€)</label>
                    <Input data-testid="insurance-indemnizacion-input" inputMode="decimal" value={form.indemnizacion} onChange={(e) => setForm({ ...form, indemnizacion: e.target.value })} />
                  </div>
                </div>
                <Input data-testid="insurance-beneficiarios-input" placeholder="Beneficiarios (ej. cónyuge e hijos)" value={form.beneficiarios} onChange={(e) => setForm({ ...form, beneficiarios: e.target.value })} />
              </div>
            )}
            <div className="flex items-center justify-between border border-border rounded-lg px-3 py-2.5">
              <div>
                <span className="text-sm block">Datos sensibles</span>
                <span className="text-xs text-muted-foreground">Capitales y beneficiarios se protegen con tu PIN secundario</span>
              </div>
              <Switch data-testid="insurance-sensitive-switch" checked={form.sensitive} onCheckedChange={(v) => setForm({ ...form, sensitive: v })} />
            </div>
            <Button data-testid="insurance-submit-button" onClick={submit} disabled={!form.name} className="w-full">{editId ? "Guardar" : "Crear seguro"}</Button>
            {editId && <MovementTrace doc={editDoc} />}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
